"use client";
import { useQueries, useQuery } from "@tanstack/react-query";
import { chunk } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslations } from "use-intl";

import { DealOfferExplorerBody } from "@/components/marketplace/deal-offer-explorer-body";
import type { OfferExplorerPayload } from "@/components/marketplace/deal-offer-explorer-types";
import {
  DealOfferPriceAlertProvider,
  useDealOfferPriceAlert,
} from "@/components/marketplace/deal-offer-price-alert-context";
import { ModalShell } from "@/components/marketplace/deal-offer-shared";
import {
  getDealOffersQueryOptions,
  type DealOffersQueryOptionsInput,
} from "@/components/marketplace/deal-offers-query";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { getReleaseAlternativePackingQueryOptions } from "@/components/marketplace/release-alternative-packing-query";
import { useLists } from "@/contexts/lists-context";
import { getModalHash } from "@/hooks/hash-modal-state";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";

/**
 * The price alert opens the add-to-lists wizard, which replaces this modal (one hash modal at a
 * time). Remember the explorer payload and reopen it once the wizard closes, so the visitor lands
 * back on the offer list instead of the bare page.
 */
function useReopenAfterPriceAlert(payload: OfferExplorerPayload | null, ownerId?: string) {
  const onPriceAlert = useDealOfferPriceAlert();
  const { wizardOpen } = useLists();
  const { open } = useHashModal();
  const pending = useRef<{ payload: OfferExplorerPayload; wizardSeen: boolean } | null>(null);

  useEffect(() => {
    const current = pending.current;
    if (!current) return;
    if (wizardOpen) {
      current.wizardSeen = true;
      return;
    }
    if (!current.wizardSeen) return;
    pending.current = null;
    const reopen = () => open("offer-explorer", current.payload, { ownerId });
    // Closing the wizard goes back in history; reopen after that navigation settles.
    if (getModalHash(window.location.hash) === "add-to-lists") {
      window.addEventListener("popstate", reopen, { once: true });
      return;
    }
    reopen();
  }, [open, ownerId, wizardOpen]);

  const alertFromModal = useCallback(() => {
    if (!onPriceAlert) return;
    if (payload) pending.current = { payload, wizardSeen: false };
    onPriceAlert();
  }, [onPriceAlert, payload]);

  return onPriceAlert ? alertFromModal : null;
}

function explorerQueryInput(input: {
  queryInput?: DealOffersQueryOptionsInput;
  releaseId?: string;
  workId?: string;
  currency: string;
  condition: "new" | "used";
}): DealOffersQueryOptionsInput {
  if (input.queryInput) return { ...input.queryInput, condition: input.condition };
  if (input.releaseId)
    return { releaseId: input.releaseId, currency: input.currency, condition: input.condition };
  return { workId: input.workId!, currency: input.currency, condition: input.condition };
}

function payloadMatchesCondition(payload: OfferExplorerPayload | null, condition: "new" | "used") {
  return (payload?.condition ?? "new") === condition;
}

export function DealOfferExplorerModal({
  offers,
  contextReleaseId,
  releaseId,
  workId,
  currency,
  ownerId,
  queryInput,
  condition = "new",
}: {
  offers: DealOffer[];
  contextReleaseId?: string;
  releaseId?: string;
  workId?: string;
  currency: string;
  ownerId?: string;
  queryInput?: DealOffersQueryOptionsInput;
  condition?: "new" | "used";
}) {
  const t = useTranslations("shared.affiliates");
  const hashModal = useHashModal();
  const { viewerId } = useViewerQueryState();
  const state = hashModal.getState<OfferExplorerPayload>("offer-explorer", { ownerId });
  const offersQuery = useQuery({
    ...getDealOffersQueryOptions(
      explorerQueryInput({ queryInput, releaseId, workId, currency, condition }),
      { viewerId },
    ),
    enabled: state.open && payloadMatchesCondition(state.payload, condition),
  });
  const loadedOffers = (offersQuery.data?.offers as DealOffer[] | undefined) ?? offers;
  const mergedOffers = useMemo(() => {
    const byId = new Map<string, DealOffer>();
    if (!offersQuery.data && state.payload?.offer)
      byId.set(state.payload.offer.offerId, state.payload.offer);
    for (const offer of loadedOffers) byId.set(offer.offerId, offer);
    return Array.from(byId.values()).filter(
      (offer) => (offer.currentCondition === "used" ? "used" : "new") === condition,
    );
  }, [condition, loadedOffers, offersQuery.data, state.payload?.offer]);
  const releaseIds = [...new Set(mergedOffers.flatMap((offer) => offer.matchedReleaseIds))];
  const releaseQueries = useQueries({
    queries: chunk(releaseIds, 100).map((ids) => ({
      ...getReleaseAlternativePackingQueryOptions({ workId: workId ?? "", releaseIds: ids }),
      enabled: state.open && Boolean(workId),
    })),
  });
  const releases = releaseQueries.flatMap((query) => query.data?.items ?? []);
  const onPriceAlert = useReopenAfterPriceAlert(state.payload, ownerId);

  return (
    <DealOfferPriceAlertProvider onPriceAlert={condition === "used" ? null : onPriceAlert}>
      <ModalShell<OfferExplorerPayload>
        title={t(condition === "used" ? "viewAllUsedOffers" : "pricesAndHistory")}
        modal="offer-explorer"
        ownerId={ownerId}
        widthClassName="md:max-w-5xl"
      >
        {({ payload }) =>
          payload && payloadMatchesCondition(payload, condition) ? (
            <DealOfferExplorerBody
              key={`${payload.offer.offerId}:${payload.format ?? "all"}`}
              payload={payload}
              offers={mergedOffers}
              contextReleaseId={contextReleaseId}
              releases={releases}
              currency={currency}
            />
          ) : null
        }
      </ModalShell>
    </DealOfferPriceAlertProvider>
  );
}
